import React, { useMemo } from "react"
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { consumptionRateAt } from "@/features/voyagePlanner/voyageSettingsFields"
import { CII_RATING_COLORS } from "@/features/voyagePlanner/fuelRequestParams"
import { parseUtcSeconds } from "@/utils/RouteProcessing"
import { PANEL_CARD_SX } from "@/theme/panelStyles"
import KpiTile, { KPI_TILE_SX } from "@/components/ui/KpiTile"
import WarnChip from "@/components/ui/WarnChip"
import ResultErrorState from "@/features/voyagePlanner/wizard/ResultErrorState"
import summaryWarnChips from "@/features/route/summaryWarnChips"

// Full-width rating tile: colored letter badge, year in the label, and —
// when target_rating (the vessel's current official rating) was sent —
// the "does this voyage keep the rating?" verdict below.
function CiiRatingTile({ cii }) {
  return (
    <Box
      sx={{
        ...KPI_TILE_SX,
        gridColumn: "1 / -1",
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "8px",
          bgcolor: CII_RATING_COLORS[cii.rating] ?? "#004f7d",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "19px",
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {cii.rating}
      </Box>
      <Box>
        <Typography sx={{ fontSize: "9px", color: "#8a98a6" }}>
          CII rating{cii.year ? ` ${cii.year}` : ""}
        </Typography>
        {cii.target_met != null ? (
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: cii.target_met ? "#2e7d32" : "#d64545",
            }}
          >
            {cii.target_met
              ? `Keeps current rating ✓ (≤ ${cii.target_rating})`
              : `Below current rating ✗ (current ${cii.target_rating})`}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: "8.5px", color: "#8a98a6" }}>
            No current rating on the vessel to compare against
          </Typography>
        )}
      </Box>
    </Box>
  )
}

function InfoBox({ badge, badgeColors, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        border: badgeColors.border,
        bgcolor: badgeColors.bg,
        borderRadius: "8px",
        px: 1.5,
        py: 1,
        mt: 0.75,
        fontSize: "11px",
        color: "#004f7d",
      }}
    >
      <Box
        sx={{
          fontSize: "9.5px",
          fontWeight: 700,
          px: 1,
          py: 0.25,
          borderRadius: "10px",
          bgcolor: badgeColors.badgeBg,
          color: badgeColors.badgeFg,
          flexShrink: 0,
        }}
      >
        {badge}
      </Box>
      {children}
    </Box>
  )
}

const formatDuration = (totalHours) => {
  const hours = Number(totalHours)
  if (!Number.isFinite(hours)) return null
  const days = Math.floor(hours / 24)
  const rest = Math.round(hours - days * 24)
  return `${days}d ${rest}h`
}

const WAVE_COLOR = "#004f7d"
const WIND_COLOR = "#1d8ed4"
const SPEED_COLOR = "#2e7d32"
const CONSUMPTION_COLOR = "#f69e00"
const LIMIT_COLOR = "#c62828"

// Combined SVG line chart of two parameters along the route on independent
// left/right axes, with optional dashed red reference lines (styling per the
// 4a design reference). left/right: { label, unit, color, values: [{ t, v }],
// refValue?, refLabel? }; samples are placed time-proportionally via xDomain.
function DualParamChart({
  title,
  subtitle,
  left: leftParam,
  right: rightParam,
  xDomain,
  xLabels,
}) {
  const W = 320
  const H = 140
  const left = 30
  const right = W - 30
  const top = 8
  const bottom = H - 24

  const axisTick = (value) => Math.round(value * 10) / 10
  // Scale tightly to the data's own min/max (not from 0) so the curve fills the
  // height. The reference/limit line does NOT widen the range — if it falls
  // outside, it is clamped to the edge and flagged.
  const buildScale = (param) => {
    const ref = Number.isFinite(param.refValue) ? param.refValue : null
    const vals = param.values.length ? param.values.map((p) => p.v) : [0]
    let lo = Math.min(...vals)
    let hi = Math.max(...vals)
    if (hi - lo < 1e-6) {
      lo -= 0.5
      hi += 0.5
    }
    const pad = (hi - lo) * 0.15
    lo -= pad
    hi += pad
    const span = hi - lo
    const y = (v) => bottom - ((v - lo) / span) * (bottom - top)
    const clampY = (v) => Math.max(top, Math.min(bottom, y(v)))
    return {
      ref,
      lo,
      hi,
      mid: (lo + hi) / 2,
      y,
      clampY,
      refOutOfRange: ref != null && (ref < lo || ref > hi),
    }
  }
  const leftScale = buildScale(leftParam)
  const rightScale = buildScale(rightParam)

  const [x0, x1] = xDomain ?? [0, 1]
  const xSpan = Math.max(x1 - x0, 1)
  const xAt = (t) => left + ((t - x0) / xSpan) * (right - left)
  const toPoints = (values, yScale) =>
    values
      .map((p) => `${xAt(p.t).toFixed(1)},${yScale(p.v).toFixed(1)}`)
      .join(" ")
  // Closed polygon (curve + baseline) for the gradient area fill.
  const toArea = (values, yScale) =>
    `${xAt(values[0].t).toFixed(1)},${bottom} ` +
    toPoints(values, yScale) +
    ` ${xAt(values[values.length - 1].t).toFixed(1)},${bottom}`

  // Unique gradient ids per chart so the two charts don't share defs.
  const gid = title.replace(/\W+/g, "-")
  const leftFill = `fill-l-${gid}`
  const rightFill = `fill-r-${gid}`

  return (
    <Box
      sx={{
        ...PANEL_CARD_SX,
        px: 1.5,
        py: 1.25,
        mt: 1.25,
      }}
    >
      <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#004f7d" }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: "10px", color: "#8a98a6", mb: 0.5 }}>
        {subtitle}
      </Typography>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: "block" }}
        role="img"
        aria-label={title}
      >
        <defs>
          <linearGradient id={leftFill} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={leftParam.color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={leftParam.color} stopOpacity="0" />
          </linearGradient>
          <linearGradient id={rightFill} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={rightParam.color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={rightParam.color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1={left} y1={top} x2={left} y2={bottom} stroke="#e2e8ee" />
        <line x1={right} y1={top} x2={right} y2={bottom} stroke="#e2e8ee" />
        <line x1={left} y1={bottom} x2={right} y2={bottom} stroke="#e2e8ee" />

        {/* left axis: unit + hi/mid/lo ticks */}
        <text x={left - 4} y={top - 1} fontSize="9" fill={leftParam.color} textAnchor="end">
          {leftParam.unit}
        </text>
        <text x={left - 4} y={top + 6} fontSize="9" fill={leftParam.color} textAnchor="end">
          {axisTick(leftScale.hi)}
        </text>
        <text
          x={left - 4}
          y={leftScale.y(leftScale.mid) + 3}
          fontSize="9"
          fill={leftParam.color}
          textAnchor="end"
        >
          {axisTick(leftScale.mid)}
        </text>
        <text x={left - 4} y={bottom + 3} fontSize="9" fill={leftParam.color} textAnchor="end">
          {axisTick(leftScale.lo)}
        </text>

        {/* right axis: unit + hi/mid/lo ticks */}
        <text x={right + 4} y={top - 1} fontSize="9" fill={rightParam.color}>
          {rightParam.unit}
        </text>
        <text x={right + 4} y={top + 6} fontSize="9" fill={rightParam.color}>
          {axisTick(rightScale.hi)}
        </text>
        <text
          x={right + 4}
          y={rightScale.y(rightScale.mid) + 3}
          fontSize="9"
          fill={rightParam.color}
        >
          {axisTick(rightScale.mid)}
        </text>
        <text x={right + 4} y={bottom + 3} fontSize="9" fill={rightParam.color}>
          {axisTick(rightScale.lo)}
        </text>

        {/* x-axis: Departure/Arrival with start/mid/end dates */}
        <text x={left} y={H - 8} fontSize="9" fill="#8a98a6">
          {xLabels ? xLabels[0] : "Departure"}
        </text>
        {xLabels?.[1] && (
          <text
            x={(left + right) / 2}
            y={H - 8}
            fontSize="9"
            fill="#8a98a6"
            textAnchor="middle"
          >
            {xLabels[1]}
          </text>
        )}
        <text x={right} y={H - 8} fontSize="9" fill="#8a98a6" textAnchor="end">
          {xLabels ? xLabels[2] : "Arrival"}
        </text>

        {/* gradient area fills under each curve */}
        {rightParam.values.length > 1 && (
          <polygon
            points={toArea(rightParam.values, rightScale.y)}
            fill={`url(#${rightFill})`}
            stroke="none"
          />
        )}
        {leftParam.values.length > 1 && (
          <polygon
            points={toArea(leftParam.values, leftScale.y)}
            fill={`url(#${leftFill})`}
            stroke="none"
          />
        )}

        {/* curves */}
        {rightParam.values.length > 1 && (
          <polyline
            points={toPoints(rightParam.values, rightScale.y)}
            fill="none"
            stroke={rightParam.color}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
        {leftParam.values.length > 1 && (
          <polyline
            points={toPoints(leftParam.values, leftScale.y)}
            fill="none"
            stroke={leftParam.color}
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* dashed reference/limit lines — red, drawn on top of the curves */}
        {leftScale.ref != null && (
          <>
            <line
              x1={left}
              y1={leftScale.clampY(leftScale.ref)}
              x2={right}
              y2={leftScale.clampY(leftScale.ref)}
              stroke={LIMIT_COLOR}
              strokeWidth="1"
              strokeDasharray="5 4"
            />
            <text
              x={left + 3}
              y={leftScale.clampY(leftScale.ref) + (leftScale.ref > leftScale.hi ? 9 : -3)}
              fontSize="8.5"
              fill={LIMIT_COLOR}
            >
              {leftScale.refOutOfRange
                ? leftScale.ref > leftScale.hi
                  ? "↑ "
                  : "↓ "
                : ""}
              {leftParam.refLabel ?? `${leftScale.ref} ${leftParam.unit}`}
            </text>
          </>
        )}
        {rightScale.ref != null && (
          <>
            <line
              x1={left}
              y1={rightScale.clampY(rightScale.ref)}
              x2={right}
              y2={rightScale.clampY(rightScale.ref)}
              stroke={LIMIT_COLOR}
              strokeWidth="1"
              strokeDasharray="5 4"
            />
            <text
              x={right - 3}
              y={rightScale.clampY(rightScale.ref) + (rightScale.ref > rightScale.hi ? 9 : -3)}
              fontSize="8.5"
              fill={LIMIT_COLOR}
              textAnchor="end"
            >
              {rightScale.refOutOfRange
                ? rightScale.ref > rightScale.hi
                  ? "↑ "
                  : "↓ "
                : ""}
              {rightParam.refLabel ?? `${rightScale.ref} ${rightParam.unit}`}
            </text>
          </>
        )}
      </svg>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          fontSize: "9.5px",
          color: "#8a98a6",
          mt: 0.5,
        }}
      >
        {[leftParam, rightParam].map((param) => (
          <Box
            key={param.label}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <Box
              sx={{ width: 14, height: 3, borderRadius: 1, bgcolor: param.color }}
            />
            {param.label} [{param.unit}]
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// Step 3 · Result — KPIs and warnings for the computed route. Renders live
// from pathfinderResponse/store state (on-map recalcs update it any time).
function ResultStep({
  isLoading,
  routeSummary,
  etaResponse,
  routeConsumption,
  pathfinderResponse,
  applyRnwSafety,
  applyEca,
  avoidHra,
  avoidJwla,
  avoidCaa,
  waveLimit,
  windLimit,
  currentLimit,
  consumptionProfileData,
  calculatorBasespeed,
  routeError,
  routeErrorContext,
  etaError,
  routeOutdated = false,
  onGoToOptions,
}) {
  // Weather/speed/consumption along the route, read from the raw waypoint
  // features (same keys the route table/WaypointCardContent use). Consumption is
  // the profile rate [t/d] interpolated at each leg's weather-adjusted speed.
  const {
    waveSeries,
    windSeries,
    speedSeries,
    consumptionSeries,
    maxWave,
    maxWind,
    maxCurrent,
    xDomain,
    xLabels,
  } = useMemo(() => {
    const features = pathfinderResponse?.features ?? []
    const points = []

    for (const feature of features) {
      if (feature?.geometry?.type !== "Point") continue
      const props = feature?.properties ?? {}
      const adjustedKmh = Number(
        props.base_speed_adjusted_kmh ?? props.baseSpeedAdjustedKmh,
      )
      const tSec = parseUtcSeconds(props.datetime_utc)
      points.push({
        t: tSec != null ? tSec * 1000 : NaN,
        wave: Number(
          props.wave_nww3_significant_height_m_interp ?? props.wave_swh,
        ),
        wind: Number(
          props.wind_gfs_speed_knots_interp ?? props.wind_speed_knots,
        ),
        current: Number(
          props.current_hycom_speed_knots_interp ?? props.current_speed_knots,
        ),
        speed: Number.isFinite(adjustedKmh)
          ? adjustedKmh / 1.852
          : Number(props.sog_knots ?? props.sog),
      })
    }

    // Time spacing needs >=2 distinct stamps, otherwise index-spacing fallback.
    const hasTimes =
      new Set(points.map((p) => p.t).filter(Number.isFinite)).size >= 2
    const samples = hasTimes
      ? points.filter((p) => Number.isFinite(p.t))
      : points.map((p, i) => ({ ...p, t: i }))
    const seriesOf = (key) =>
      samples
        .filter((p) => Number.isFinite(p[key]))
        .map((p) => ({ t: p.t, v: p[key] }))
    const waves = seriesOf("wave")
    const winds = seriesOf("wind")
    const speeds = seriesOf("speed")

    // Per-point consumption rate [t/d] at each leg's weather-adjusted speed
    // (same interpolation as computeTotalConsumption).
    const consumptions = speeds
      .map(({ t, v }) => ({ t, v: consumptionRateAt(v, consumptionProfileData) }))
      .filter(({ v }) => Number.isFinite(v))

    const ts = samples.map((p) => p.t)
    const domain = ts.length ? [Math.min(...ts), Math.max(...ts)] : null

    // Start / temporal-midpoint / end date labels (MM-DD) for the x-axis.
    const fmtDay = (ms) => {
      const d = new Date(ms)
      const p = (n) => String(n).padStart(2, "0")
      return `${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`
    }
    let labels = null
    if (hasTimes) {
      labels = [
        fmtDay(domain[0]),
        fmtDay((domain[0] + domain[1]) / 2),
        fmtDay(domain[1]),
      ]
      if (labels[1] === labels[0] || labels[1] === labels[2]) labels[1] = null
    }

    // Maxima over all points (not the time-filtered samples) so the KPI limit
    // warnings see values whose waypoint lacks a timestamp.
    const maxOf = (key) => {
      const vals = points.map((p) => p[key]).filter(Number.isFinite)
      return vals.length ? Math.max(...vals) : null
    }

    return {
      waveSeries: waves,
      windSeries: winds,
      speedSeries: speeds,
      consumptionSeries: consumptions,
      maxWave: maxOf("wave"),
      maxWind: maxOf("wind"),
      maxCurrent: maxOf("current"),
      xDomain: domain,
      xLabels: labels,
    }
  }, [pathfinderResponse, consumptionProfileData])

  const numericWaveLimit = Number(waveLimit)
  const numericWindLimit = Number(windLimit)

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          py: 8,
          color: "#8a98a6",
          fontSize: "12.5px",
        }}
      >
        <CircularProgress size={28} thickness={4} sx={{ color: "#004f7d" }} />
        Calculating route …
      </Box>
    )
  }

  if (routeError) {
    return <ResultErrorState routeError={routeError} {...routeErrorContext} />
  }

  const hasOutput =
    etaResponse || routeConsumption || pathfinderResponse || etaError

  if (!hasOutput) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          py: 8,
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: "12px", color: "#8a98a6" }}>
          No route has been calculated yet.
        </Typography>
        <Button
          size="small"
          onClick={onGoToOptions}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 600,
            color: "#004f7d",
          }}
        >
          Go to Options and run the calculation
        </Button>
      </Box>
    )
  }

  const warnChips = summaryWarnChips(routeSummary)

  const duration = formatDuration(routeSummary?.total_time_hour)

  return (
    <Box sx={{ pt: 0.5 }} data-tour="planner-result">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 0.75,
          mb: 1.25,
        }}
      >
        {etaResponse && (
          <KpiTile label="ETA (UTC)" value={String(etaResponse).slice(0, 16)} />
        )}
        {duration && <KpiTile label="Duration" value={duration} />}
        {routeSummary?.total_distance_km != null && (
          <KpiTile
            label="Distance"
            value={`${Number(routeSummary.total_distance_km).toFixed(0)} km`}
          />
        )}
        {(routeSummary?.total_consumption_tons != null ||
          (routeConsumption != null && routeConsumption !== false)) && (
          <KpiTile
            label="Consumption M/E"
            // astar's per-leg total (needs a consumption profile in the
            // request); the local time×rate estimate is only the fallback.
            value={`${Number(
              routeSummary?.total_consumption_tons ?? routeConsumption,
            ).toFixed(1)} t`}
          />
        )}
        {routeSummary?.total_fuel_cost_usd != null && (
          <KpiTile
            label="Fuel cost"
            value={`$${Math.round(
              Number(routeSummary.total_fuel_cost_usd),
            ).toLocaleString("en-US")}`}
            subValue={
              routeSummary?.eca_fuel_cost_usd != null
                ? `thereof ECA: $${Math.round(
                    Number(routeSummary.eca_fuel_cost_usd),
                  ).toLocaleString("en-US")}`
                : null
            }
          />
        )}
        {maxWave != null && (
          <KpiTile
            label="Max. Wave"
            value={`${maxWave.toFixed(1)} m`}
            valueColor={
              maxWave > (Number.isFinite(numericWaveLimit) ? numericWaveLimit : 5)
                ? "#b7791f"
                : "#2e7d32"
            }
          />
        )}
        {maxWind != null && (
          <KpiTile
            label="Max. Wind"
            value={`${maxWind.toFixed(1)} kn`}
            valueColor={
              Number.isFinite(numericWindLimit) && maxWind > numericWindLimit
                ? "#b7791f"
                : "#2e7d32"
            }
          />
        )}
        {routeSummary?.cii?.rating && (
          <CiiRatingTile cii={routeSummary.cii} />
        )}
      </Box>

      {routeSummary && (
        <Typography sx={{ fontSize: "11px", color: "#57728d", mb: 0.75 }}>
          {applyRnwSafety ? "RNW-filtered" : "Weather-filtered"}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {warnChips.map((chip) => (
          <WarnChip key={chip.key} severity={chip.severity}>
            {chip.label}
          </WarnChip>
        ))}
        {routeSummary && warnChips.length === 0 && (
          <WarnChip severity="success">Route is clear</WarnChip>
        )}
        {etaError && <WarnChip severity="error">{String(etaError)}</WarnChip>}
        {routeOutdated && (
          <WarnChip severity="warning">
            Route changed — run Calculate ETA to update
          </WarnChip>
        )}
      </Box>

      {(waveSeries.length > 1 || windSeries.length > 1) && (
        <DualParamChart
          title="Wave & wind along the route"
          subtitle="Dashed red lines mark the configured limits"
          left={{
            label: "Wave",
            unit: "m",
            color: WAVE_COLOR,
            values: waveSeries,
            refValue: numericWaveLimit,
          }}
          right={{
            label: "Wind",
            unit: "kn",
            color: WIND_COLOR,
            values: windSeries,
            refValue: numericWindLimit,
          }}
          xDomain={xDomain}
          xLabels={xLabels}
        />
      )}

      {(speedSeries.length > 1 || consumptionSeries.length > 1) && (
        <DualParamChart
          title="Speed & consumption along the route"
          subtitle="Weather-adjusted speed — dashed red line marks the base speed"
          left={{
            label: "Speed",
            unit: "kn",
            color: SPEED_COLOR,
            values: speedSeries,
            refValue: Number(calculatorBasespeed),
            refLabel: `base ${calculatorBasespeed} kn`,
          }}
          right={{
            label: "Consumption",
            unit: "t/d",
            color: CONSUMPTION_COLOR,
            values: consumptionSeries,
          }}
          xDomain={xDomain}
          xLabels={xLabels}
        />
      )}

      {applyEca && (
        <InfoBox
          badge="ECA"
          badgeColors={{
            border: "1px solid #cfe0ec",
            bg: "#f4f8fb",
            badgeBg: "#dbe9f5",
            badgeFg: "#004f7d",
          }}
        >
          Emission Control Areas are factored into this route calculation.
        </InfoBox>
      )}
      {avoidHra && (
        <InfoBox
          badge="HRA"
          badgeColors={{
            border: "1px solid #f3dfc0",
            bg: "#fdf8ef",
            badgeBg: "#f7ecd8",
            badgeFg: "#b7791f",
          }}
        >
          Piracy high risk areas are avoided in this route calculation.
        </InfoBox>
      )}
      {avoidJwla && (
        <InfoBox
          badge="JWC"
          badgeColors={{
            border: "1px solid #e3c4cf",
            bg: "#fbf3f5",
            badgeBg: "#f0dae2",
            badgeFg: "#7b1e3c",
          }}
        >
          JWC war risk areas are avoided in this route calculation; ports inside
          a listed area stay reachable.
        </InfoBox>
      )}
      {avoidCaa && (
        <InfoBox
          badge="RISK"
          badgeColors={{
            border: "1px solid #bfdff0",
            bg: "#f1f8fc",
            badgeBg: "#d6ecf7",
            badgeFg: "#0277bd",
          }}
        >
          Operational risk areas (ice, freak waves, gap winds, fog, munitions,
          fishing grounds) are avoided in this route calculation; seasonal areas
          apply by departure month.
        </InfoBox>
      )}
    </Box>
  )
}

export default React.memo(ResultStep)
